import { createContext, JSX, ReactNode, useCallback, useContext, useEffect, useMemo } from 'react';
import { useOnboardedUserLazyQuery } from '../graphQL';

type AccessValidationState = {
  /**
   * Whether the user can proceed with their intended action
   * - false: still validating or has validation errors
   * - true: validation complete and user has access
   */
  canProceed: boolean;
  /**
   * Triggers the access validation check
   */
  validateAccess: () => void;
};

const AccessValidationContext = createContext<AccessValidationState | null>(null);

type AccessValidationProviderProps = {
  children: ReactNode;
};

export const AccessValidationProvider = ({
  children,
}: AccessValidationProviderProps): JSX.Element => {
  const [fetchOnboardedUser, { loading, error, called }] = useOnboardedUserLazyQuery();

  // Stable function identity so downstream effects don't re-run endlessly
  const validateAccess = useCallback((): void => {
    // Always trigger a validation check; Apollo will dedupe if an identical request is inflight
    void fetchOnboardedUser();
  }, [fetchOnboardedUser]);

  const hasError = error != null;
  const canProceed = called && !loading && !hasError;

  const contextValue: AccessValidationState = useMemo(
    () => ({ canProceed, validateAccess }),
    [canProceed, validateAccess],
  );

  return (
    <AccessValidationContext.Provider value={contextValue}>
      {children}
    </AccessValidationContext.Provider>
  );
};

export const useAccessValidation = (): AccessValidationState => {
  const context = useContext(AccessValidationContext);
  if (context === null) {
    throw new Error('useAccessValidation must be used within an AccessValidationProvider');
  }
  return context;
};

/**
 * Hook that automatically triggers access validation on mount.
 * This is used as a safety net in AppLayout to ensure access validation
 * happens even if the user navigates directly to a protected page.
 */
export const useAccessValidationSafetyCheck = (): void => {
  const { validateAccess } = useAccessValidation();

  useEffect(() => {
    // Automatically trigger validation as a safety net
    // If already called, this will be a no-op
    validateAccess();
  }, [validateAccess]);
};
